import { useState } from 'react';
import { apiClient, ApiError, type SourceCreated } from '@/lib/api/client';
import { useDialogFocus } from '@/lib/useDialogFocus';

interface Props {
  onCreated: (source: SourceCreated) => void;
  onClose: () => void;
}

export function NewSourceModal({ onCreated, onClose }: Props) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const formRef = useDialogFocus<HTMLFormElement>();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await apiClient.createSource(name.trim());
      onCreated(r);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to create source');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-source-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <form
        ref={formRef}
        onSubmit={submit}
        className="w-full max-w-md space-y-5 rounded-2xl border border-border bg-card p-7 shadow-2xl"
      >
        <div className="space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            New source
          </div>
          <h2 id="new-source-title" className="text-2xl font-semibold tracking-tight">Connect a source</h2>
          <p className="text-sm text-muted-foreground">
            A source represents an external system that sends events into this workspace's inbox.
            Give it a recognizable name (DECT, TeraBridge, Stripe webhooks).
          </p>
        </div>
        <div className="space-y-2">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
            maxLength={64}
            placeholder="DECT App"
            className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>
        {err && (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            {err}
          </div>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md border border-border bg-background px-4 py-2.5 text-sm text-muted-foreground hover:border-ring/60 hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || busy}
            className="flex-1 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy ? 'Creating…' : 'Create source'}
          </button>
        </div>
      </form>
    </div>
  );
}
